import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { selectedOptionIds } from '@/components/assessments/assessmentResponse';
import { QuizFileDropzone } from '@/components/assessments/QuizFileDropzone';
import { hasQuizAnswer } from '@/components/assessments/quizFileAnswer';
import { remainingUntilDeadline } from '@/lib/attempt-clock';

export interface QuizQuestion {
  id: string;
  index: number;
  stem: string;
  type?: string;
  options?: { id: string; label: string; content: string }[];
}

function computeRemainingFromStartedAt(startedAt: string, timeLimitSeconds: number): number {
  const elapsed = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000);
  return Math.max(0, timeLimitSeconds - elapsed);
}

export interface QuizPlayerProps {
  questions: QuizQuestion[];
  timeLimitSeconds?: number | null;
  remainingSeconds?: number | null;
  startedAt?: string | null;
  deadlineAt?: string | null;
  clockOffsetMs?: number;
  initialAnswers?: Record<string, unknown>;
  onLocalChange?: (answers: Record<string, unknown>) => void;
  onSubmit?: (answers: Record<string, unknown>) => void;
  onSaveProgress?: (answers: Record<string, unknown>) => void;
  onUploadFile?: (questionId: string, file: File) => Promise<unknown>;
  submitting?: boolean;
  submitted?: boolean;
  queued?: boolean;
  saveStatus?: string | null;
  warnBeforeUnload?: boolean;
  finishLabel?: string;
  submittedLabel?: string;
  confirmTitle?: string;
  confirmDescription?: string;
  confirmSubmitLabel?: string;
}

function formatTime(seconds: number | null): string {
  if (seconds === null) return '';
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function QuizPlayer({
  questions,
  timeLimitSeconds = null,
  remainingSeconds = null,
  startedAt = null,
  deadlineAt = null,
  clockOffsetMs = 0,
  initialAnswers = {},
  onLocalChange,
  onSubmit,
  onSaveProgress,
  onUploadFile,
  submitting = false,
  submitted = false,
  queued = false,
  saveStatus = null,
  warnBeforeUnload = false,
  finishLabel = 'Terminar intento',
  submittedLabel = 'Intento enviado',
  confirmTitle = 'Terminar intento',
  confirmDescription = '¿Deseas enviar tu intento? No podrás modificar las respuestas después.',
  confirmSubmitLabel = 'Enviar intento',
}: QuizPlayerProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const saveTimerRef = useRef<number | null>(null);
  const [current, setCurrent] = useState(0);
  const [answered, setAnswered] = useState<Record<number, boolean>>({});
  const [answers, setAnswers] = useState<Record<string, unknown>>(initialAnswers);
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const initialRemaining = remainingSeconds ?? timeLimitSeconds;
  const [remaining, setRemaining] = useState<number | null>(initialRemaining);
  const [showConfirm, setShowConfirm] = useState(false);
  const [fileBusyId, setFileBusyId] = useState<string | null>(null);
  const [fileErrors, setFileErrors] = useState<Record<string, string>>({});
  const emptyExpirySent = useRef(false);
  const timedSubmitSent = useRef(false);
  const total = questions.length;
  const hasAnswer = questions.some((question) => hasQuizAnswer(answers[question.id]));
  const timeUp = remaining === 0;
  const locked = submitting || submitted || queued || timeUp;
  const finishText = submitting ? 'Enviando…' : queued ? 'Envío en cola' : submitted ? submittedLabel : finishLabel;
  const questionKey = questions.map((question) => question.id).join('|');
  const initialAnswersKey = JSON.stringify(initialAnswers);

  useEffect(() => {
    answersRef.current = initialAnswers;
    setAnswers(initialAnswers);
    const answeredMap: Record<number, boolean> = {};
    questions.forEach((question) => {
      if (hasQuizAnswer(initialAnswers[question.id])) answeredMap[question.index] = true;
    });
    setAnswered(answeredMap);
    setCurrent(0);
  }, [initialAnswersKey, questionKey]);

  useEffect(() => {
    if (deadlineAt) {
      const tick = () => setRemaining(remainingUntilDeadline(deadlineAt, clockOffsetMs));
      tick();
      const interval = window.setInterval(tick, 1000);
      return () => window.clearInterval(interval);
    }

    if (startedAt && timeLimitSeconds != null && timeLimitSeconds > 0) {
      const tick = () => setRemaining(computeRemainingFromStartedAt(startedAt, timeLimitSeconds));
      tick();
      const interval = window.setInterval(tick, 1000);
      return () => window.clearInterval(interval);
    }

    if (initialRemaining === null || initialRemaining <= 0) {
      setRemaining(null);
      return;
    }

    setRemaining(initialRemaining);
    const interval = window.setInterval(() => {
      setRemaining((prev) => (prev === null ? null : Math.max(0, prev - 1)));
    }, 1000);

    return () => window.clearInterval(interval);
  }, [deadlineAt, clockOffsetMs, startedAt, timeLimitSeconds, initialRemaining]);

  useEffect(() => {
    if (!warnBeforeUnload) return undefined;
    const onLeave = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, [warnBeforeUnload]);

  useEffect(() => {
    if (remaining !== 0 || submitting || submitted || timedSubmitSent.current) return;
    timedSubmitSent.current = true;
    formRef.current?.requestSubmit();
  }, [remaining, submitting, submitted]);

  const queueSaveProgress = (nextAnswers: Record<string, unknown>) => {
    if (!onSaveProgress || locked) return;
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => {
      onSaveProgress(nextAnswers);
    }, 700);
  };

  const publishAnswers = (next: Record<string, unknown>) => {
    answersRef.current = next;
    setAnswers(next);
    onLocalChange?.(next);
    queueSaveProgress(next);
  };

  const updateAnswer = (questionId: string, value: unknown, questionIndex: number) => {
    if (locked) return;
    publishAnswers({ ...answersRef.current, [questionId]: value });
    setAnswered((prev) => ({ ...prev, [questionIndex]: hasQuizAnswer(value) }));
  };

  const uploadQuestionFile = async (question: QuizQuestion, file: File) => {
    if (locked || !onUploadFile) return;
    setFileBusyId(question.id);
    setFileErrors((prev) => ({ ...prev, [question.id]: '' }));
    try {
      const uploaded = await onUploadFile(question.id, file);
      updateAnswer(question.id, uploaded, question.index);
    } catch (error) {
      setFileErrors((prev) => ({
        ...prev,
        [question.id]: error instanceof Error ? error.message : 'No se pudo subir el archivo',
      }));
    } finally {
      setFileBusyId(null);
    }
  };

  const toggleMultipleOption = (questionId: string, optionId: string, questionIndex: number) => {
    if (locked) return;
    const current = selectedOptionIds(answersRef.current[questionId]);
    const nextIds = current.includes(optionId)
      ? current.filter((id) => id !== optionId)
      : [...current, optionId];
    publishAnswers({ ...answersRef.current, [questionId]: nextIds });
    setAnswered((prev) => ({ ...prev, [questionIndex]: nextIds.length > 0 }));
  };

  const goTo = (index: number) => {
    if (locked || index < 0 || index >= total) return;
    setCurrent(index);
    const block = document.getElementById(`quiz-question-${index}`);
    block?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting || submitted) return;
    if (!hasAnswer) {
      setShowConfirm(false);
      if (remaining !== 0 || emptyExpirySent.current) return;
      emptyExpirySent.current = true;
      onSubmit?.(answers);
      return;
    }
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    onSaveProgress?.(answers);
    onSubmit?.(answers);
  };

  const emptySubmitHint = !hasAnswer
    ? timeUp
      ? queued
        ? 'Se acabó el tiempo. El cierre quedó en cola y se enviará al recuperar la conexión.'
        : 'Se acabó el tiempo y no respondiste ninguna pregunta. El examen no se envió.'
      : 'Responde al menos una pregunta para enviar el examen.'
    : null;

  if (total === 0) {
    return (
      <div className="lms-quiz-attempt__empty">
        <span className="material-symbols-outlined" aria-hidden="true">
          help
        </span>
        <h2>Sin preguntas</h2>
        <p>La evaluación no tiene preguntas vinculadas desde el banco.</p>
      </div>
    );
  }

  return (
    <form ref={formRef} className="lms-quiz-attempt__form" onSubmit={handleSubmit}>
      <fieldset disabled={locked} className="border-0 p-0 m-0 min-w-0">
        <div className="lms-quiz-attempt__layout">
          <div className="lms-quiz-attempt__main">
            {questions.map((question) => (
              <article
                key={question.id}
                id={`quiz-question-${question.index}`}
                data-question-index={question.index}
                className="lms-quiz-attempt__card scroll-mt-6"
              >
                <div className="lms-quiz-question__label">
                  Pregunta <span>{question.index + 1}</span>
                </div>
                <div className="lms-quiz-question__stem">{question.stem}</div>

                {question.type === 'true_false' ? (
                  <fieldset className="lms-quiz-question__options">
                    {[
                      { value: 'true', label: 'Verdadero' },
                      { value: 'false', label: 'Falso' },
                    ].map((option) => (
                      <label key={option.value} className="lms-quiz-question__option">
                        <input
                          type="radio"
                          className="lms-quiz-question__input"
                          name={`answer[${question.id}]`}
                          value={option.value}
                          checked={answers[question.id] === option.value}
                          onChange={() => updateAnswer(question.id, option.value, question.index)}
                        />
                        <span className="lms-quiz-question__option-text">{option.label}</span>
                      </label>
                    ))}
                  </fieldset>
                ) : question.type === 'multiple_choice' && question.options && question.options.length > 0 ? (
                  <fieldset className="lms-quiz-question__options">
                    {question.options.map((opt) => {
                      const selected = selectedOptionIds(answers[question.id]);
                      return (
                        <label key={opt.id} className="lms-quiz-question__option">
                          <input
                            type="checkbox"
                            className="lms-quiz-question__input"
                            name={`answer[${question.id}][]`}
                            value={opt.id}
                            checked={selected.includes(opt.id)}
                            onChange={() => toggleMultipleOption(question.id, opt.id, question.index)}
                          />
                          <span className="lms-quiz-question__option-text">
                            {opt.label ? (
                              <>
                                <span className="lms-quiz-question__option-letter">{opt.label}.</span>
                                {opt.content}
                              </>
                            ) : (
                              opt.content
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </fieldset>
                ) : question.type === 'short_answer' || question.type === 'long_answer' ? (
                  question.type === 'long_answer' ? (
                    <textarea
                      name={`answer[${question.id}]`}
                      className="lms-quiz-question__text-input"
                      rows={5}
                      placeholder="Escribe tu respuesta…"
                      value={(() => {
                        const textAnswer = answers[question.id];
                        return typeof textAnswer === 'string' ? textAnswer : '';
                      })()}
                      onChange={(event) => updateAnswer(question.id, event.target.value, question.index)}
                    />
                  ) : (
                    <input
                      type="text"
                      name={`answer[${question.id}]`}
                      className="lms-quiz-question__text-input"
                      placeholder="Tu respuesta"
                      value={(() => {
                        const textAnswer = answers[question.id];
                        return typeof textAnswer === 'string' ? textAnswer : '';
                      })()}
                      onChange={(event) => updateAnswer(question.id, event.target.value, question.index)}
                    />
                  )
                ) : question.type === 'file_upload' ? (
                  <QuizFileDropzone
                    name={`answer[${question.id}]`}
                    value={answers[question.id]}
                    disabled={locked || !onUploadFile}
                    busy={fileBusyId === question.id}
                    error={fileErrors[question.id] || null}
                    onFile={(file) => void uploadQuestionFile(question, file)}
                    onClear={() => updateAnswer(question.id, '', question.index)}
                  />
                ) : question.options && question.options.length > 0 ? (
                  <fieldset className="lms-quiz-question__options">
                    {question.options.map((opt) => (
                      <label key={opt.id} className="lms-quiz-question__option">
                        <input
                          type="radio"
                          className="lms-quiz-question__input"
                          name={`answer[${question.id}]`}
                          value={opt.id}
                          checked={answers[question.id] === opt.id}
                          onChange={() => updateAnswer(question.id, opt.id, question.index)}
                        />
                        <span className="lms-quiz-question__option-text">
                          {opt.label ? (
                            <>
                              <span className="lms-quiz-question__option-letter">{opt.label}.</span>
                              {opt.content}
                            </>
                          ) : (
                            opt.content
                          )}
                        </span>
                      </label>
                    ))}
                  </fieldset>
                ) : (
                  <input
                    type="text"
                    name={`answer[${question.id}]`}
                    className="lms-quiz-question__text-input"
                    placeholder="Tu respuesta"
                    value={(() => {
                      const textAnswer = answers[question.id];
                      return typeof textAnswer === 'string' ? textAnswer : '';
                    })()}
                    onChange={(event) => updateAnswer(question.id, event.target.value, question.index)}
                  />
                )}
              </article>
            ))}

            <div className="lms-quiz-attempt__footer">
              {emptySubmitHint && (
                <p className="lms-quiz-attempt__submit-hint" id="quiz-empty-submit-hint">
                  {emptySubmitHint}
                </p>
              )}
              <button
                type="button"
                className="lms-quiz-attempt__finish"
                disabled={locked || !hasAnswer}
                aria-describedby={emptySubmitHint ? 'quiz-empty-submit-hint' : undefined}
                onClick={() => {
                  if (!hasAnswer) return;
                  setShowConfirm(true);
                }}
              >
                {finishText}
              </button>
            </div>
          </div>

          <aside className="lms-quiz-attempt__aside">
            <div className="lms-quiz-attempt__nav">
              {remaining !== null && (
                <div
                  className={`lms-quiz-attempt__timer${remaining <= 60 ? ' lms-quiz-attempt__timer--danger' : ''}`}
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    timer
                  </span>
                  <span>
                    Tiempo restante: <strong>{formatTime(remaining)}</strong>
                  </span>
                </div>
              )}
              {saveStatus && (
                <p className="lms-quiz-attempt__save" role="status" aria-live="polite">
                  {saveStatus}
                </p>
              )}

              <h3 className="lms-quiz-attempt__nav-title">Preguntas</h3>

              <div className="lms-quiz-attempt__grid" role="navigation" aria-label="Navegación por el cuestionario">
                {questions.map((q) => (
                  <button
                    key={q.id}
                    type="button"
                    className={`lms-quiz-attempt__grid-btn${
                      current === q.index
                        ? ' lms-quiz-attempt__grid-btn--current'
                        : answered[q.index]
                          ? ' lms-quiz-attempt__grid-btn--answered'
                          : ''
                    }`}
                    onClick={() => goTo(q.index)}
                    disabled={locked}
                    aria-label={`Ir a la pregunta ${q.index + 1}`}
                    aria-current={current === q.index ? 'true' : undefined}
                  >
                    {q.index + 1}
                  </button>
                ))}
              </div>

              {emptySubmitHint && (
                <p className="lms-quiz-attempt__submit-hint lms-quiz-attempt__submit-hint--aside">{emptySubmitHint}</p>
              )}
              <button
                type="button"
                className="lms-quiz-attempt__finish-outline"
                disabled={locked || !hasAnswer}
                onClick={() => {
                  if (!hasAnswer) return;
                  setShowConfirm(true);
                }}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  check_circle
                </span>
                {queued ? 'Envío en cola' : submitted ? submittedLabel : `${finishLabel}…`}
              </button>
            </div>
          </aside>
        </div>
      </fieldset>

      {showConfirm && !locked && (
        <div className="lms-quiz-attempt__modal-overlay" role="presentation" onClick={() => setShowConfirm(false)}>
          <div
            className="lms-quiz-attempt__modal-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="quiz-finish-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="lms-quiz-attempt__modal">
              <h3 id="quiz-finish-title">{confirmTitle}</h3>
              <p>{confirmDescription}</p>
              <div className="lms-quiz-attempt__modal-actions">
                <button
                  type="button"
                  className="lms-quiz-attempt__modal-btn lms-quiz-attempt__modal-btn--ghost"
                  onClick={() => setShowConfirm(false)}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="lms-quiz-attempt__modal-btn lms-quiz-attempt__modal-btn--primary"
                  disabled={submitting || !hasAnswer}
                >
                  {confirmSubmitLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}
